import * as React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { ArrowForward } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router';
import { useNotification } from './CustomNotification';
import {useLanguage} from "../../contexts/LanguageContext";
import {
  getOne as getOrder,
  updateOne as updateOrder,
  validate as validateOrder,
  type Order,
} from '../data/orders';
import {
  searchByMobileNumber,
  createOne as createCustomer,
} from '../data/customers';
import OrderForm, {
  type FormFieldValue,
  type OrderFormState,
} from './OrderForm';
import PageContainer from './PageContainer';

function OrderEditForm({
  initialValues,
  onSubmit,
}: {
  initialValues: Partial<OrderFormState['values']>;
  onSubmit: (formValues: Partial<OrderFormState['values']>) => Promise<void>;
}) {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const notifications = useNotification();

  const [formState, setFormState] = React.useState<OrderFormState>(() => ({
    values: initialValues,
    errors: {},
  }));
  const formValues = formState.values;
  const formErrors = formState.errors;

  const setFormValues = React.useCallback(
    (newFormValues: Partial<OrderFormState['values']>) => {
      setFormState((previousState) => ({
        ...previousState,
        values: newFormValues,
      }));
    },
    [],
  );

  const setFormErrors = React.useCallback(
    (newFormErrors: Partial<OrderFormState['errors']>) => {
      setFormState((previousState) => ({
        ...previousState,
        errors: newFormErrors,
      }));
    },
    [],
  );

  const handleFormFieldChange = React.useCallback(
    (name: keyof OrderFormState['values'], value: FormFieldValue) => {
      const validateField = async (values: Partial<OrderFormState['values']>) => {
        const { issues } = validateOrder(values);
        const errorMessage = issues?.find((issue) => issue.path?.[0] === name)?.message;
        setFormErrors({
          ...formErrors,
          [name]: errorMessage ? t(errorMessage) : undefined,
        });
      };

      const newFormValues = { ...formValues, [name]: value };

      setFormValues(newFormValues);
      validateField(newFormValues);
    },
    [formValues, formErrors, setFormErrors, setFormValues, t],
  );

  const handleFormReset = React.useCallback(() => {
    setFormValues(initialValues);
  }, [initialValues, setFormValues]);

  const handleFormSubmit = React.useCallback(async () => {
    const { issues } = validateOrder(formValues);
    if (issues && issues.length > 0) {
      setFormErrors(
        Object.fromEntries(issues.map((issue) => [issue.path?.[0], t(issue.message)])),
      );
      return;
    }
    setFormErrors({});

    try {
      let customerId: number;

      // Step 1: Search for customer by mobile number
      const existingCustomer = await searchByMobileNumber(formValues.customerNumber || '');

      if (existingCustomer) {
        // Step 2: Customer exists, use their ID
        customerId = existingCustomer.id;
      } else {
        // Step 3: Customer doesn't exist, create a new one
        const newCustomer = await createCustomer({
          name: formValues.customerName || '',
          mobile_number: formValues.customerNumber || '',
        } as any);
        customerId = newCustomer.id;
      }

      // Add customer_id to form values before submitting
      const updatedFormValues = {
        ...formValues,
        customer_id: customerId,
      };

      await onSubmit(updatedFormValues);
      notifications.show(t('messages.updateSuccess'), {
        severity: 'success',
        autoHideDuration: 3000,
      });

      navigate('/orders');
    } catch (editError) {
      notifications.show(
        `${t('messages.updateFailed')} ${(editError as Error).message}`,
        {
          severity: 'error',
          autoHideDuration: 3000,
        },
      );
      throw editError;
    }
  }, [formValues, navigate, notifications, onSubmit, setFormErrors]);

  return (
    <OrderForm
      formState={formState}
      onFieldChange={handleFormFieldChange}
      onSubmit={handleFormSubmit}
      onReset={handleFormReset}
      submitButtonLabel={t('common.save')}
      backButtonPath={`/orders/${orderId}`}
    />
  );
}

export default function OrderEdit() {
  const { orderId } = useParams();
  const { t } = useLanguage();
  const [order, setOrder] = React.useState<Order | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<Error | null>(null);

  const loadData = React.useCallback(async () => {
    setError(null);
    setIsLoading(true);

    try {
      const showData = await getOrder(Number(orderId));

      // Map customer data to form fields
      const mappedData = {
        ...showData,
        customerName: showData.customer?.name || showData.customerName,
        customerNumber: showData.customer?.mobile_number || showData.customerNumber,
      };

      setOrder(mappedData);
    } catch (showDataError) {
      setError(showDataError as Error);
    }
    setIsLoading(false);
  }, [orderId]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSubmit = React.useCallback(
    async (formValues: Partial<OrderFormState['values']>) => {
      const updatedData = await updateOrder(Number(orderId), formValues);
      setOrder(updatedData);
    },
    [orderId],
  );

  const renderEdit = React.useMemo(() => {
    if (isLoading) {
      return (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            m: 1,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }
    if (error) {
      return (
        <Box sx={{ flexGrow: 1 }}>
          <Alert severity="error">{error.message}</Alert>
        </Box>
      );
    }

    return order ? (
      <OrderEditForm initialValues={order} onSubmit={handleSubmit} />
    ) : null;
  }, [isLoading, error, order, handleSubmit]);

  const { direction } = useLanguage();
  const isRtl = direction === 'rtl';
  const navigate = useNavigate();

  const handleBack = React.useCallback(() => {
    navigate(`/orders/${orderId}`);
  }, [navigate, orderId]);

  return (
    <PageContainer
      breadcrumbs={[
        { title: t('orders.title'), path: '/orders' },
        { title: t('orders.view'), path: `/orders/${orderId}` },
        { title: t('orders.edit') },
      ]}
      backButton={
        <IconButton
          onClick={handleBack}
          sx={(theme) => ({
            backgroundColor: 'transparent',
            borderRadius: '50%',
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : '#31394a',
              color: theme.palette.mode === 'dark'
                ? theme.palette.text.primary
                : 'white'
            },
          })}
          aria-label="back"
        >
          {isRtl ? <ArrowForward /> : <ArrowBackIcon />}
        </IconButton>
      }
    >
      <Box sx={{ display: 'flex', flex: 1 }}>{renderEdit}</Box>
    </PageContainer>
  );
}

